"use client";
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import Category from "@/components/Home/Category";
import FullPageScroll from "@/components/Home/FullPageScroll";
import type { Blog } from "@/lib/@types/api/blog";

type HomeSectionsProps = {
  categories: unknown;
  blogList: Blog.BlogListItem[];
  children?: ReactNode;
};

type AsyncComponentModule = {
  default: ComponentType<any>;
};

const loadIndustryApplications = () => import("@/components/Home/IndustryApplications") as Promise<AsyncComponentModule>;
const loadAboutBlince = () => import("@/components/Home/AboutBlince") as Promise<AsyncComponentModule>;
const loadAdvantageCard = () => import("@/components/Home/AdvantageCard") as Promise<AsyncComponentModule>;
const loadNews = () => import("@/components/Home/News") as Promise<AsyncComponentModule>;
const loadFooter = () => import("@/components/Footer/page") as Promise<AsyncComponentModule>;

function DeferredSection({
  loader,
  componentProps,
  renderLoaded,
  placeholderClassName,
  rootMargin = "320px 0px",
}: {
  loader: () => Promise<AsyncComponentModule>;
  componentProps?: Record<string, unknown>;
  renderLoaded?: (LoadedComponent: ComponentType<any>) => ReactNode;
  placeholderClassName: string;
  rootMargin?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [LoadedComponent, setLoadedComponent] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    if (shouldRender) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    const element = containerRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, shouldRender]);

  useEffect(() => {
    if (!shouldRender || LoadedComponent) {
      return;
    }

    let isActive = true;
    loader().then((module) => {
      if (isActive) {
        setLoadedComponent(() => module.default);
      }
    });

    return () => {
      isActive = false;
    };
  }, [LoadedComponent, loader, shouldRender]);

  return (
    <div ref={containerRef} className="h-full">
      {LoadedComponent
        ? renderLoaded
          ? renderLoaded(LoadedComponent)
          : <LoadedComponent {...componentProps} />
        : <div aria-hidden="true" className={placeholderClassName} />}
    </div>
  );
}

export default function HomeSections({ categories, blogList, children }: HomeSectionsProps) {
  const deferredSections = [
    <DeferredSection
      key="industry"
      loader={loadIndustryApplications}
      placeholderClassName="min-h-[72vh] md:h-full"
    />,
    <DeferredSection
      key="about"
      loader={loadAboutBlince}
      placeholderClassName="min-h-[72vh] md:h-full"
    />,
    <DeferredSection
      key="advantage"
      loader={loadAdvantageCard}
      placeholderClassName="min-h-[72vh] md:h-full"
    />,
    <DeferredSection
      key="news"
      loader={loadNews}
      componentProps={{ blogList }}
      placeholderClassName="min-h-[72vh] md:h-full"
    />,
    <DeferredSection
      key="footer"
      loader={loadFooter}
      placeholderClassName="min-h-[40vh] md:h-full"
      rootMargin="480px 0px"
      renderLoaded={(LoadedFooter) => (
        <div className="flex items-end h-full">
          <LoadedFooter />
        </div>
      )}
    />,
  ];

  return (
    <FullPageScroll>
      {children}
      <Category categories={categories} />
      {deferredSections}
    </FullPageScroll>
  );
}
